import styled, { css } from "@rentiohq/web-theme";
import { RM_COLORS } from "../../rentmatch.colors";

interface ITabProps {
  isActive?: boolean;
  isDragOver?: boolean;
  isDropDisabled?: boolean;
  isFirst?: boolean;
  isLast?: boolean;
  isArchive?: boolean;
  phase?: 1 | 2 | 3;
}

const ARROW = 14; /* depth of the arrow tip */

export const TabsContainer = styled.div<{ isDragging?: boolean }>`
  display: flex;
  align-items: stretch;
  background: transparent;
  overflow-x: ${({ isDragging }) => (isDragging ? "hidden" : "auto")};
  overflow-y: hidden;
  gap: 16px;
  padding: ${({ theme }) => theme.spacing("base")}
    ${({ theme }) => theme.spacing("loose")};
  margin: -24px -24px 0 -24px;
  width: calc(100% + 48px);

  /* Hide scrollbar across browsers */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE/Edge */
  &::-webkit-scrollbar {
    display: none; /* Chrome/Safari */
  }
`;

export const PhaseGroup = styled.div<{ isArchive?: boolean }>`
  display: flex;
  align-items: stretch;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 0 0 1px rgba(63, 63, 68, 0.1);
  flex-shrink: ${({ isArchive }) => (isArchive ? 1 : 0)};
  flex: ${({ isArchive }) => (isArchive ? 1 : "none")};
  min-width: ${({ isArchive }) => (isArchive ? "160px" : "auto")};
`;

export const Tab = styled.div<ITabProps>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px 10px 18px;
  cursor: pointer;
  white-space: nowrap;
  user-select: none;
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
  transition:
    background 150ms ease,
    color 150ms ease;
  flex-shrink: 0;

  /* Default: left flat + right arrow */
  clip-path: polygon(
    0 0,
    calc(100% - ${ARROW}px) 0,
    100% 50%,
    calc(100% - ${ARROW}px) 100%,
    0 100%
  );

  /* Non-first tabs get left notch too */
  ${({ isFirst }) =>
    !isFirst &&
    css`
      clip-path: polygon(
        0 0,
        calc(100% - ${ARROW}px) 0,
        100% 50%,
        calc(100% - ${ARROW}px) 100%,
        0 100%,
        ${ARROW}px 50%
      );
      padding-left: ${ARROW + 10}px;
    `}

  /* Last tab in group: flat right edge (no arrow) */
  ${({ isLast, isFirst }) =>
    isLast &&
    css`
      clip-path: ${isFirst
        ? "polygon(0 0, 100% 0, 100% 100%, 0 100%)"
        : `polygon(0 0, 100% 0, 100% 100%, 0 100%, ${ARROW}px 50%)`};
      padding-right: 18px;
    `}

  /* Archive: no arrows, plain text */
  ${({ isArchive }) =>
    isArchive &&
    css`
      clip-path: none;
      padding: 0 8px;
      color: ${({ theme }) => theme.getColor("gray", 400)};
      background: transparent !important;
      font-size: 11px;
      font-weight: 600;
      letter-spacing: 0.05em;
      text-transform: uppercase;
      &:hover {
        background: transparent !important;
        color: ${({ theme }) => theme.getColor("gray", 600)};
      }
    `}

  background: ${({ theme }) => theme.getColor("gray", 0)};
  color: ${({ theme }) => theme.getColor("gray", 800)};
  font-weight: 500;

  &:hover {
    background: ${RM_COLORS.border};
    color: ${({ theme }) => theme.getColor("gray", 800)};
  }

  ${({ isActive }) =>
    isActive &&
    css`
      background: ${({ theme }) => theme.getColor("green", 0)};
      color: ${({ theme }) => theme.getColor("green", 1000)};
      font-weight: 600;
    `}

  ${({ isDragOver }) =>
    isDragOver &&
    css`
      background: ${({ theme }) => theme.getColor("green", 200)} !important;
      color: ${({ theme }) => theme.getColor("green", 1000)} !important;
    `}

  ${({ isDropDisabled }) =>
    isDropDisabled &&
    css`
      cursor: not-allowed;
      opacity: 0.6;
    `}
`;

export const TabBadge = styled.span<{ isActive?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  background: ${({ isActive, theme }) =>
    isActive ? theme.getColor("green", 1000) : theme.getColor("gray", 200)};
  color: ${({ isActive, theme }) =>
    isActive ? "white" : theme.getColor("gray", 600)};
`;

// Reserve the width of the bold (active) label via a hidden phantom copy, so
// switching the active tab never shifts the layout (bold text is wider than the
// regular weight, which otherwise makes the row jump).
export const TabLabel = styled.span`
  display: inline-flex;
  flex-direction: column;

  &::after {
    content: attr(data-label);
    height: 0;
    font-weight: 600;
    visibility: hidden;
    overflow: hidden;
    pointer-events: none;
  }
`;

/* No longer needed — gap handles spacing */
export const TabSeparator = styled.div`
  display: none;
`;

export const ArchiveSeparator = styled.div`
  display: none;
`;
